import { createContext, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logger } from "@/lib/logger";

const AuthContext = createContext(null);

// Legacy localStorage keys we used before httpOnly cookies. Cleared on mount
// so an old browser session doesn't keep a token sitting around in storage.
const LEGACY_KEYS = ["cs_token", "cs_user"];

function clearLegacyStorage() {
  try {
    LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (err) {
    // localStorage can be disabled in private mode — log + move on, no real
    // recovery action exists here. Surfacing this to the user would be noise.
    logger.debug("[auth] legacy storage scrub failed:", err);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    clearLegacyStorage();
    api
      .get("/auth/me")
      .then((res) => setUser(res.data))
      .catch((err) => {
        // 401 (no valid session) is expected on first visit — only log
        // unexpected statuses so log noise stays signal.
        if (err?.response?.status && err.response.status !== 401) {
          logger.error("[auth] /auth/me failed:", err);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (payload) => {
    const res = await api.post("/auth/register", payload);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Even if the network call fails the local user state is cleared so the
      // UI immediately reflects a logged-out experience. Surface for ops.
      logger.error("[auth] logout dispatch failed:", err);
    }
    setUser(null);
  };

  // Memoize the context value so a parent re-render doesn't force every
  // consumer to re-render with a fresh object identity. user-only dep set
  // matches the actual reactive surface — login/register/logout are stable.
  const value = useMemo(
    () => ({ user, login, register, logout, loading }),
    [user, loading],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
